'use client';

import { useCallback, useMemo, useState } from 'react';
import logError from '@alltrails/core/utils/logError';
import FilterChip from '@alltrails/denali/components/FilterChip';
import type { IconDefinition } from '@alltrails/denali/components/IconRenderer';
import Community from '@alltrails/denali/icons/Community';
import Globe from '@alltrails/denali/icons/Globe';
import Lock from '@alltrails/denali/icons/Lock';
import { setRoutePrivacy } from '@alltrails/maps/redux/customRoute';
import Modal from '@alltrails/modal/components/Modal/Modal';
import PrivacyModal from '@alltrails/privacy/components/PrivacyModal/PrivacyModal';
import updateMapPrivacy from '@alltrails/privacy/utils/requests';
import type { PrivacyPolicySubjects } from '@alltrails/types/PrivacyPolicySubjects';
import { type Visibility, VISIBILITY_FOLLOWERS_ONLY, VISIBILITY_PRIVATE, VISIBILITY_PUBLIC } from '@alltrails/types/Visibility';
import { useAppDispatch } from '@/utils/redux';

type PrivacyChipProps = {
  mapId: number;
  routePrivacy: Visibility;
  canEdit: boolean;
  privacyPolicySubject: PrivacyPolicySubjects;
  onPrivacyClicked?: () => void;
  onPrivacyChanged?: (policy: Visibility) => void;
};

const getPrivacyIconComponent = (privacyOption: Visibility) => {
  switch (privacyOption) {
    case VISIBILITY_PUBLIC:
      return Globe;
    case VISIBILITY_FOLLOWERS_ONLY:
      return Community;
    case VISIBILITY_PRIVATE:
      return Lock;
    default:
      return Globe;
  }
};

const PrivacyChip = ({ mapId, routePrivacy, canEdit, privacyPolicySubject, onPrivacyClicked, onPrivacyChanged }: PrivacyChipProps) => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const privacyIcon: IconDefinition<'orientation'> = useMemo(() => ({ Component: getPrivacyIconComponent(routePrivacy) }), [routePrivacy]);

  const openModal = useCallback(() => {
    onPrivacyClicked?.();
    setIsModalOpen(true);
  }, [onPrivacyClicked]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const onPrivacyChange = useCallback(
    (policy: Visibility) => {
      onPrivacyChanged?.(policy);
      const previousPrivacy = routePrivacy;
      dispatch(setRoutePrivacy(policy));
      updateMapPrivacy(mapId, policy)
        .then(response => {
          const { maps } = response;
          if (maps && maps.length > 0) {
            setIsModalOpen(false);
          } else {
            dispatch(setRoutePrivacy(previousPrivacy));
            logError(new Error('PrivacyChip: updateMapPrivacy returned no maps'));
          }
        })
        .catch(error => {
          dispatch(setRoutePrivacy(previousPrivacy));
          logError(new Error('PrivacyChip: failed to update map privacy', { cause: error }));
        });
    },
    [dispatch, mapId, onPrivacyChanged, routePrivacy]
  );

  if (!canEdit) {
    return null;
  }

  return (
    <>
      <FilterChip onClick={openModal} size="sm" unselectedText="" icon={privacyIcon} />
      <Modal isOpen={isModalOpen} onCloseRequest={closeModal}>
        <PrivacyModal
          privacyPolicySubject={privacyPolicySubject}
          onPrivacyChange={onPrivacyChange}
          privacyPolicy={routePrivacy}
          closeModal={closeModal}
          hasCustomStyles
        />
      </Modal>
    </>
  );
};

export default PrivacyChip;
